
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { ProductSearchDropdown } from "./ProductSearchDropdown";

interface Product {
  id: string;
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  hasCredit: boolean;
  posStatus?: 'found' | 'not-found';
  catalogName?: string;
}

interface ProductListSearchProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  showSearchDropdown: boolean;
  onShowSearchDropdown: (show: boolean) => void;
  filteredProducts: Product[];
  onProductSelect: (product: Product) => void;
  posStatusFilter: "all" | "found" | "not-found";
  onPosStatusFilterChange: (filter: "all" | "found" | "not-found") => void;
  allProducts: Product[] | undefined;
}

export const ProductListSearch = ({
  searchQuery,
  onSearchChange,
  showSearchDropdown,
  onShowSearchDropdown,
  filteredProducts,
  onProductSelect,
  posStatusFilter,
  onPosStatusFilterChange,
  allProducts
}: ProductListSearchProps) => {
  return (
    <div className="space-y-4 mb-4">
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            type="text"
            placeholder="Search products by name or barcode..."
            value={searchQuery}
            onChange={(e) => {
              onSearchChange(e.target.value);
              onShowSearchDropdown(e.target.value.length > 0);
            }}
            className="pl-10"
            onFocus={() => onShowSearchDropdown(searchQuery.length > 0)}
          />
        </div>
        
        {showSearchDropdown && (
          <ProductSearchDropdown
            products={filteredProducts}
            onSelect={onProductSelect}
            onClose={() => onShowSearchDropdown(false)}
          />
        )}
      </div>

      {/* POS Status Filter */}
      <div className="flex gap-2 items-center">
        <span className="text-sm font-medium text-gray-700">Filter by POS Status:</span>
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
          <Button
            variant={posStatusFilter === "all" ? "default" : "ghost"}
            onClick={() => onPosStatusFilterChange("all")}
            size="sm"
            className="h-8 text-xs"
          >
            All ({Array.isArray(allProducts) ? allProducts.length : 0})
          </Button>
          <Button
            variant={posStatusFilter === "found" ? "default" : "ghost"}
            onClick={() => onPosStatusFilterChange("found")}
            size="sm"
            className="h-8 text-xs flex items-center gap-1"
          >
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            In POS ({Array.isArray(allProducts) ? allProducts.filter(p => p.posStatus === 'found').length : 0})
          </Button>
          <Button
            variant={posStatusFilter === "not-found" ? "default" : "ghost"}
            onClick={() => onPosStatusFilterChange("not-found")}
            size="sm"
            className="h-8 text-xs flex items-center gap-1"
          >
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            Not in POS ({Array.isArray(allProducts) ? allProducts.filter(p => p.posStatus === 'not-found').length : 0})
          </Button>
        </div>
      </div>
    </div>
  );
};
